import { computed } from 'vue'
import { cloneDeep, isNil } from 'lodash'
import { v4 as uuidv4 } from 'uuid'

import { isRequired } from '@shared/utils/CustomErrors'
import { updatePersonInBasket } from '@shared/utils/Person'

import contractStartDateAPI from '@shared/api/contractStartDate'
import basketStore from '@shared/store/basket'
import useBasket from '@shared/hooks/useBasket'
import useI18n from '@shared/hooks/useI18n'

export default function usePerson() {
  // HOOKS
  const { isExistingCustomerBasket } = useBasket()
  const { t } = useI18n()

  // COMPUTED
  const persons = computed(() => {
    return cloneDeep(basketStore.basket.persons || [])
  })

  const firstPersonId = computed(() => {
    return persons.value.length > 0 ? persons.value[0].personId : undefined
  })

  // METHODS
  async function createPerson(prefill = {}) {
    const defaultContracStartDate = await contractStartDateAPI.getDefaultContractStartDate()

    return {
      order: null,
      contractModified: true,
      collectivePersonGroup: null,
      immigrant: false,
      kvgContractStartDate: defaultContracStartDate.kvgContractStartDate,
      vvgContractStartDate: defaultContracStartDate.vvgContractStartDate,
      personData: {
        dateOfBirth: prefill.dateOfBirth ?? null,
        firstName: prefill.firstName ?? null,
        lastName: prefill.lastName ?? null,
        gender: prefill.gender ?? null,
      },
      personId: prefill.personId ?? uuidv4(),
    }
  }

  function getPerson(personId) {
    return persons.value.find(p => p.personId === personId)
  }

  function getPersonFullName(personId) {
    if (!personId) return null

    const person = getPerson(personId)

    if (!person || !person.personData) {
      return t('person.person', { number: persons.value.length + 1 })
    }

    const { firstName = '', lastName = '' } = person.personData
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim()
    }

    if (!person.personData.order) {
      const personIndex = persons.value.findIndex(x => x.personId === personId)
      return t('person.person', { number: personIndex + 1 })
    }

    return t('person.person', { number: person.order })
  }

  /**
   * filterPersonsToValidate returns all the persons which need to be validated to proceed
   * As a basket on for an existing customer (from the service portal channel switch) always
   * contains all the persons of the contract, only the one's that have their contract modified
   * needs to be validated
   * @param {basket} payload
   * @return {Array} persons
   */
  function filterPersonsToValidate(payload) {
    const { existingCustomer, persons: __persons } = payload
    return existingCustomer ? __persons.filter(p => p.contractModified) : __persons
  }

  function getWorkSituationFields({ isVVGOnly, isKVGOnly }, workSituationData) {
    if (isNil(isVVGOnly) || isNil(isKVGOnly)) {
      throw new Error('Missing required parameters')
    }

    const fields = []

    if (askWorkSituation(isVVGOnly)) {
      fields.push({ name: 'workSituation', required: true })
    }

    if (askSecondaryEmployment(isKVGOnly)) {
      fields.push({ name: 'secondaryEmployment', required: true })
      if (workSituationData.secondaryEmployment === true) {
        fields.push({ name: 'professionSecondaryEmployment', required: true })
      }
    }

    return fields
  }

  function askWorkSituation(isVVGOnly) {
    return !isVVGOnly
  }

  function askSecondaryEmployment(isKVGOnly) {
    return !isKVGOnly
  }

  /**
   * @param {String} personId
   * @returns {Number} index - index of the person in the basket
   */
  function getPersonIndex(personId) {
    return persons.value.findIndex(p => p.personId === personId)
  }

  function getPersonByPartnerNumber(partnerNumber) {
    return persons.value.find(p => p.partnerNumber === partnerNumber)
  }

  function isUnborn(personId) {
    const person = getPerson(personId)
    return person.age < 0
  }

  function addOrderToPersons(unorderedPersons) {
    return unorderedPersons.map((person, index) => {
      return {
        ...person,
        order: index + 1,
      }
    })
  }

  function isExistingCustomer(personId = isRequired('personId')) {
    if (!isExistingCustomerBasket.value) return false

    const person = getPerson(personId)
    return !!person.partnerNumber
  }

  async function updatePerson(payload) {
    const { formData, person } = payload

    const personData = Object.assign({}, person.personData, formData.personData)

    const response = updatePersonInBasket(cloneDeep(basketStore.basket), person.personId, {
      kvgContractStartDate: formData.kvgContractStartDate,
      vvgContractStartDate: formData.vvgContractStartDate,
      personData,
    })

    // FLEX-993 delete cancellation data if contract start date changed after cancellation was answered
    if (person.vvgContractStartDate !== formData.vvgContractStartDate && !isNil(response.cancellationAnswer)) {
      response.cancellationAnswer = null
    }

    return await basketStore.updateOvpBasket(response)
  }

  return {
    // COMPUTED
    firstPersonId,
    persons,

    // METHODS
    addOrderToPersons,
    createPerson,
    filterPersonsToValidate,
    getPerson,
    getPersonFullName,
    getPersonByPartnerNumber,
    getPersonIndex,

    getWorkSituationFields,
    isExistingCustomer,
    isUnborn,
    updatePerson,
  }
}
